import { Heart, Sprout, Users, Handshake, Sun, Target, Telescope } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";

const VALUES = [
  { icon: Heart, title: "Empathy", text: "We listen, understand and approach people with compassion." },
  { icon: Sprout, title: "Growth", text: "We believe everyone has the ability to learn and grow." },
  { icon: Users, title: "Inclusivity", text: "Everyone deserves to feel heard, respected and included." },
  { icon: Handshake, title: "Community", text: "Positive change becomes stronger when people support one another." },
  { icon: Sun, title: "Well-Being", text: "We encourage healthier and more balanced approaches to everyday life." },
];

export default function About() {
  return (
    <>
      <SEO
        title="About Nurturing Happiness | Our Mission & Vision"
        description="Nurturing Happiness is a community-focused initiative dedicated to promoting mental wellness, learning, personal growth and positive living."
      />

      <section data-testid="about-header" className="paper-texture py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-nh-green">About Us</p>
            <h1 className="mt-3 max-w-3xl font-heading text-4xl font-bold tracking-tight text-nh-ink sm:text-5xl">
              About Nurturing Happiness
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-nh-muted md:text-lg">
              Nurturing Happiness is a community-focused initiative dedicated to promoting mental
              wellness, learning, personal growth and positive living.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-who-we-are" className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 md:grid-cols-2 md:px-12">
          <Reveal>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-3xl bg-nh-fresh/30" />
              <img
                src="https://images.pexels.com/photos/38386424/pexels-photo-38386424.jpeg"
                alt="An elder sharing learning materials with smiling young people in a sunlit community garden"
                loading="lazy"
                className="relative rounded-3xl object-cover shadow-soft md:aspect-[4/3] md:w-full"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-nh-ink sm:text-4xl">
              Who We Are
            </h2>
            <p className="mt-6 text-base leading-relaxed text-nh-muted md:text-lg">
              We believe that well-being is connected to many parts of life — how we learn, how we
              understand ourselves, how we connect with others and how we approach everyday
              challenges.
            </p>
            <p className="mt-4 text-base leading-relaxed text-nh-muted md:text-lg">
              Our aim is to create awareness, encourage learning and develop initiatives that
              support individual growth and stronger communities.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-mission-vision" className="py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 md:grid-cols-2 md:px-12">
          <Reveal>
            <div className="h-full rounded-3xl bg-nh-deep p-10 text-white shadow-soft transition-all duration-300 hover:-translate-y-2 hover:shadow-lift md:p-12">
              <Target className="h-10 w-10 text-nh-yellow" strokeWidth={1.75} />
              <h2 className="mt-6 font-heading text-2xl font-bold sm:text-3xl">Our Mission</h2>
              <p className="mt-4 text-base leading-relaxed text-nh-cream/85">
                To nurture healthier minds, encourage continuous learning and create supportive
                communities where individuals can learn, grow and thrive.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full rounded-3xl border border-nh-gold/30 bg-amber-50 p-10 shadow-soft transition-all duration-300 hover:-translate-y-2 hover:shadow-lift md:p-12">
              <Telescope className="h-10 w-10 text-nh-gold" strokeWidth={1.75} />
              <h2 className="mt-6 font-heading text-2xl font-bold text-nh-ink sm:text-3xl">Our Vision</h2>
              <p className="mt-4 text-base leading-relaxed text-nh-body">
                A world where mental well-being, learning and positive community connections are
                accessible to everyone.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-values" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-nh-green">What Guides Us</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-nh-ink sm:text-4xl">
              Our Values
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div
                  data-testid={`value-card-${v.title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="h-full rounded-3xl border border-nh-deep/5 bg-nh-cream p-8 shadow-soft transition-all duration-300 hover:-translate-y-2 hover:shadow-lift"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-nh-mint text-nh-green">
                    <v.icon className="h-7 w-7" strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-6 font-heading text-xl font-semibold uppercase tracking-wide text-nh-deep">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-nh-muted">{v.text}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.4}>
              <div className="flex h-full items-center justify-center rounded-3xl bg-nh-green p-10 text-center shadow-soft">
                <p data-testid="about-final-statement" className="font-heading text-2xl font-semibold italic leading-snug text-white">
                  Learn. Grow. Connect. Nurture Happiness.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
